import { connect } from "react-redux";
import { Collapse } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import classname from "classnames";
import { useEffect, useMemo, useState } from "react";
import { selectOrgCustomization } from "src/store/orgCustomizations/reducer";
import { findActiveMenuMatch } from "../../menuPathUtils";

const LoadingComponent = () => {
    return <></>;
};

const HorizontalMenuContent = (props) => {
    const { menu, t, menuOpen, modules, organization, orgCustomization } = props;
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    // Dropdown open state is manual only — never kept open from the URL.
    const [manualOpenIndex, setManualOpenIndex] = useState(-1);

    const urlActiveTopIndex = useMemo(() => {
        const match = findActiveMenuMatch(menu || [], location.pathname);
        return match?.indexes?.[0] ?? -1;
    }, [menu, location.pathname]);

    useEffect(() => {
        // Close any open dropdown after navigation (incl. submenu Link clicks).
        setManualOpenIndex(-1);
    }, [location.pathname]);

    useEffect(() => {
        setIsLoading(!(!modules.isLoading && modules.featuresLoadingStatus === "loaded"));
    }, [modules.isLoading, modules.featuresLoadingStatus]);

    const toggleDropdown = (index, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        setManualOpenIndex((current) => (current === index ? -1 : index));
    };

    const renderTitle = (item, keyName = "menuItem") => {
        return typeof item.titleKey === "function"
            ? item.titleKey({
                  t,
                  organization,
                  orgCustomization,
                  [keyName]: item,
              })
            : t(item.titleKey);
    };

    return (
        <Collapse isOpen={menuOpen} className="navbar-collapse" id="topnav-menu-content">
            <ul className="navbar-nav">
                {(() => {
                    if (isLoading) {
                        return <LoadingComponent />;
                    }

                    return (menu || []).map((menuItem, index) => {
                        const subMenuItems = menuItem.items || [];
                        const hasSubMenu = subMenuItems.length > 0;
                        const isSectionActive = urlActiveTopIndex === index;
                        const isDropdownOpen = manualOpenIndex === index;

                        return (
                            <li
                                key={index}
                                className={
                                    "nav-item" +
                                    (hasSubMenu ? " dropdown" : "") +
                                    (isSectionActive ? " active" : "")
                                }
                            >
                                {menuItem.linkType === "react-router" ? (
                                    menuItem?.path ? (
                                        <Link
                                            className={
                                                "nav-link" +
                                                (hasSubMenu ? " dropdown-toggle arrow-none" : "") +
                                                (isSectionActive ? " active" : "")
                                            }
                                            to={menuItem.path}
                                            onClick={
                                                hasSubMenu
                                                    ? (event) => toggleDropdown(index, event)
                                                    : undefined
                                            }
                                        >
                                            <i className={menuItem?.icon + " me-2"}></i>
                                            {renderTitle(menuItem)}
                                        </Link>
                                    ) : (
                                        <span
                                            className={
                                                "nav-link" +
                                                (hasSubMenu ? " dropdown-toggle arrow-none" : "") +
                                                (isSectionActive ? " active" : "")
                                            }
                                            role={hasSubMenu ? "button" : undefined}
                                            onClick={
                                                hasSubMenu
                                                    ? (event) => toggleDropdown(index, event)
                                                    : undefined
                                            }
                                        >
                                            <i className={menuItem?.icon + " me-2"}></i>
                                            {renderTitle(menuItem)}
                                        </span>
                                    )
                                ) : menuItem?.path ? (
                                    <a
                                        className={
                                            "nav-link" + (isSectionActive ? " active" : "")
                                        }
                                        href={menuItem.path}
                                        target="_new"
                                    >
                                        <i className={menuItem?.icon + " me-2"}></i>
                                        <span className="ms-1">{renderTitle(menuItem)}</span>
                                    </a>
                                ) : (
                                    <span
                                        className={
                                            "nav-link" +
                                            (hasSubMenu ? " dropdown-toggle arrow-none" : "") +
                                            (isSectionActive ? " active" : "")
                                        }
                                        role={hasSubMenu ? "button" : undefined}
                                        onClick={
                                            hasSubMenu
                                                ? (event) => toggleDropdown(index, event)
                                                : undefined
                                        }
                                    >
                                        <i className={menuItem?.icon + " me-2"}></i>
                                        <span className="ms-1">{renderTitle(menuItem)}</span>
                                    </span>
                                )}

                                {hasSubMenu ? (
                                    <div
                                        className={classname("dropdown-menu dropdown-menu-end", {
                                            show: isDropdownOpen,
                                        })}
                                        aria-labelledby="topnav-apps"
                                    >
                                        {subMenuItems.map((subMenuItem, subMenuItemIndex) => {
                                            const subMatch = findActiveMenuMatch(
                                                [subMenuItem],
                                                location.pathname
                                            );
                                            const subActive = !!subMatch;

                                            return subMenuItem.linkType === "react-router" ? (
                                                <Link
                                                    key={subMenuItemIndex}
                                                    to={subMenuItem?.path}
                                                    className={
                                                        "dropdown-item" +
                                                        (subActive ? " active" : "")
                                                    }
                                                >
                                                    {renderTitle(subMenuItem, "subMenuItem")}
                                                </Link>
                                            ) : (
                                                <a
                                                    key={subMenuItemIndex}
                                                    className={
                                                        "dropdown-item" +
                                                        (subActive ? " active" : "")
                                                    }
                                                    href={subMenuItem?.path}
                                                    target="_new"
                                                >
                                                    {renderTitle(subMenuItem, "subMenuItem")}
                                                </a>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </li>
                        );
                    });
                })()}
            </ul>
        </Collapse>
    );
};

const mapStatetoProps = (state) => {
    const { Modules: modules, Organization: organization } = state;
    return {
        modules,
        organization,
        orgCustomization: selectOrgCustomization(state, organization?.id),
    };
};

export default withNamespaces()(
    connect(mapStatetoProps, {})(HorizontalMenuContent)
);
